import { Trash2Icon } from 'lucide-react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import styles from './RegexCard.module.scss';

type RegexOption = { slug: string; trigger_message: string; opening_message: string };

interface RegexCardProps<FormType extends FieldValues> {
  index: number;
  onDelete: () => void;
  onBlur?: () => void;
  control: Control<FormType>;
  readOnly?: boolean;
}

const RegexCard = <FormType extends FieldValues>({
  index,
  onBlur,
  onDelete,
  control,
  readOnly = false,
}: RegexCardProps<FormType>) => {
  const { t } = useTranslation();

  const slugRules = {
    required: t('common.required_field'),
    maxLength: { value: 512, message: t('validation.less_than', { 0: 512 }) },
  };
  const messageRules = {
    required: t('validation.required'),
    maxLength: { value: 64, message: t('validation.at_least', { 0: 64 }) },
  };

  // Each field reads its error from its own Controller: the parent's `errors` object is mutated
  // in place, so a compiled parent would never re-render this card when it changes.
  const renderField = (
    key: keyof RegexOption,
    label: string,
    placeholder: string,
    rules: typeof slugRules
  ) => {
    const id = `options.${index}.${key}`;
    return (
      <div className="w-full">
        <Controller
          control={control}
          name={id as Path<FormType>}
          rules={rules}
          render={({ field, fieldState: { error } }) => (
            <>
              <Label htmlFor={id} className={cn('mb-2', !!error && 'text-warning')}>
                {label}
              </Label>
              <Input
                id={id}
                placeholder={placeholder}
                aria-invalid={!!error}
                readOnly={readOnly}
                {...field}
                value={field.value ?? ''}
                onBlur={() => {
                  field.onBlur();
                  onBlur?.();
                }}
              />
              {error?.message && <p className="mt-1 text-sm text-warning">{error.message}</p>}
            </>
          )}
        />
      </div>
    );
  };

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.levelOne}>
          {renderField(
            'slug',
            t('integrations.start.regexp'),
            t('integrations.start.type'),
            slugRules
          )}
        </div>
        <div className={styles.levelTwo}>
          {renderField(
            'opening_message',
            t('integrations.start.teaser'),
            t('integrations.start.type_message'),
            messageRules
          )}
          {renderField(
            'trigger_message',
            t('integrations.start.auto'),
            t('integrations.start.type_message'),
            messageRules
          )}
        </div>
        <span className={styles.delete}>
          <Button
            className="hover:text-destructive hover:[&_svg]:text-destructive"
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('integrations.start.remove_regexp')}
            onClick={onDelete}
          >
            <Trash2Icon size={16} />
          </Button>
        </span>
      </div>
    </>
  );
};

export default RegexCard;
